/**
 * Registers a brand new key (`665:20548`) -- the only place the secret ever
 * exists client-side, and only for the span of one request. Renewing an
 * existing key is `RenewKeyDialog`, a separate component: that dialog keeps
 * the key's own name and posts to `/ops/keys/{k}/renew`, so it has no Nome
 * field to fill in, and this one has no grace control to offer since nothing
 * here is being superseded.
 *
 * The secret field is cleared synchronously, before the request settles
 * either way -- a retry is exactly the moment nobody is watching for a
 * lingering copy. `useRegisterKey` sets `gcTime: 0` for the same reason, but
 * that only takes effect once the mutation's observer detaches, which is
 * what the unconditional `mutation.reset()` in `finally` does. The
 * on-screen failure is kept in local state rather than read from the
 * mutation, precisely so that reset can be unconditional.
 */

import { useId, useState, type FormEvent, type ReactNode } from 'react';

import { ApiFailureError, type ApiFailure } from '../../lib/http/errors';
import { useRegisterKey } from '../api/queries';
import { FormDialog, fieldControlProps, fieldErrorOf } from '../../chrome/form';
import { Field, FieldLabel, FieldError } from '../../ui/field';
import { Input } from '../../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { operationProviderSchema, type OperationProvider } from '../api/schemas';
import { t as operationsT } from '../operations/messages';
import { displayKeyFailure, kt } from './messages';

/** Every provider a key can authenticate to: `platform` calls nobody. */
const KEY_PROVIDERS = operationProviderSchema.options.filter((p) => p !== 'platform');

/** The kit's `ErrorState` renders a conflict as "Could not complete: {the
 *  server's own message}" -- correct for a message nobody has scripted, but
 *  this form has its own sentence, and `failure.invalid`'s template is a
 *  bare `{message}` with no wrapper. Recasting the 409 as an unfielded
 *  `invalid` puts that sentence on screen unwrapped. The secret's own 422
 *  gets the same treatment so it reads as this form's sentence rather than
 *  whatever the server phrased it as. A bare 500 (`server`) is recast the
 *  same way into `key.error.generic` rather than the kit's app-wide
 *  "Something went wrong" -- `offline` and the auth-shaped kinds are left
 *  alone, since those already say something more specific than this form
 *  could. */

export function NewKeyDialog(props: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): ReactNode {
  const [name, setName] = useState('');
  const [provider, setProvider] = useState<OperationProvider | undefined>(undefined);
  const [secret, setSecret] = useState('');
  // Read from local state, never from `mutation.error`: the cleanup below
  // calls `mutation.reset()` unconditionally, and reading the mutation's own
  // error would wipe the message the instant it clears.
  const [failure, setFailure] = useState<ApiFailure | null>(null);
  const registerMutation = useRegisterKey();
  const labelId = useId();
  const providerId = useId();
  const secretId = useId();
  const secretErrorId = useId();

  function reset(): void {
    setName('');
    setProvider(undefined);
    setSecret('');
    setFailure(null);
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<unknown> {
    event.preventDefault();
    const submittedSecret = secret;
    // Cleared before the request settles either way -- see this file's doc
    // comment.
    setSecret('');
    if (provider === undefined) return Promise.reject(new Error('no provider'));
    return registerMutation
      .mutateAsync({ name, provider, secret: submittedSecret })
      .catch((error: unknown) => {
        setFailure(
          error instanceof ApiFailureError
            ? displayKeyFailure(error.failure)
            : { kind: 'server', status: 500 },
        );
        throw error;
      })
      .finally(() => registerMutation.reset());
  }

  return (
    <FormDialog
      open={props.open}
      onOpenChange={(open) => {
        if (!open) reset();
        props.onOpenChange(open);
      }}
      title={kt('new.title')}
      description={kt('new.subtitle')}
      submitLabel={kt('new.submit')}
      pending={registerMutation.isPending}
      failure={failure}
      onSubmit={handleSubmit}
    >
      <Field>
        <FieldLabel htmlFor={labelId}>{kt('new.name.label')}</FieldLabel>
        <Input
          id={labelId}
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder={kt('new.name.placeholder')}
          required
        />
      </Field>
      <Field>
        <FieldLabel htmlFor={providerId}>{kt('new.provider.label')}</FieldLabel>
        <Select
          value={provider}
          onValueChange={(value) => setProvider(value as OperationProvider)}
          required
        >
          <SelectTrigger id={providerId}>
            <SelectValue placeholder={kt('new.provider.placeholder')} />
          </SelectTrigger>
          <SelectContent>
            {KEY_PROVIDERS.map((p) => (
              <SelectItem key={p} value={p}>
                {operationsT(`provider.${p}` as const)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      <Field data-invalid={fieldErrorOf(failure, 'secret') !== null || undefined}>
        <FieldLabel htmlFor={secretId}>{kt('new.secret.label')}</FieldLabel>
        <Input
          id={secretId}
          type="password"
          autoComplete="off"
          spellCheck={false}
          value={secret}
          onChange={(event) => setSecret(event.target.value)}
          required
          {...fieldControlProps(failure, 'secret', secretErrorId)}
        />
        <FieldError id={secretErrorId}>{fieldErrorOf(failure, 'secret')}</FieldError>
      </Field>
    </FormDialog>
  );
}
